import { create } from 'zustand';
import type { SlabAnalyzeRequest, SlabType } from './types';

interface SlabState {
  request: SlabAnalyzeRequest;
  /** Per-type saved values so switching types preserves previous edits. */
  requests: Record<SlabType, SlabAnalyzeRequest>;
  setType: (t: SlabType) => void;
  setSection: (patch: Partial<SlabAnalyzeRequest['section']>) => void;
  setMaterials: (patch: Partial<SlabAnalyzeRequest['materials']>) => void;
  setLoads: (patch: Partial<SlabAnalyzeRequest['loads']>) => void;
  setReinforcement: (patch: Partial<SlabAnalyzeRequest['reinforcement']>) => void;
  /** Replace the active request — used when loading a saved design. */
  loadRequest: (req: SlabAnalyzeRequest) => void;
}

function defaultsFor(type: SlabType): SlabAnalyzeRequest {
  const baseMat = { fc: 210, fy: 4200, cover: 2.5, TMA: 1.27 };
  const baseLoads = { WD: 400, WL: 200 };
  switch (type) {
    case 'aligerada_1d':
      return {
        section: {
          type, L_span: 400, h_total: 20, bw: 10, s: 40, t_slab: 5,
          continuity: 'one_end_continuous', Lb: 0, continuity_long: 'one_end_continuous',
        },
        materials: baseMat,
        loads: baseLoads,
        reinforcement: {},
      };
    case 'maciza_1d':
      return {
        section: {
          type, L_span: 400, h_total: 15, bw: 100, s: 100, t_slab: 15,
          continuity: 'one_end_continuous', Lb: 0, continuity_long: 'one_end_continuous',
        },
        materials: baseMat,
        loads: baseLoads,
        reinforcement: {},
      };
    case 'aligerada_2d':
      return {
        section: {
          type, L_span: 400, h_total: 20, bw: 10, s: 40, t_slab: 5,
          continuity: 'both_ends_continuous', Lb: 500, continuity_long: 'both_ends_continuous',
        },
        materials: baseMat,
        loads: baseLoads,
        reinforcement: {},
      };
    case 'maciza_2d':
      return {
        section: {
          type, L_span: 400, h_total: 17, bw: 100, s: 100, t_slab: 17,
          continuity: 'both_ends_continuous', Lb: 500, continuity_long: 'both_ends_continuous',
        },
        materials: baseMat,
        loads: baseLoads,
        reinforcement: {},
      };
  }
}

const initialRequests: Record<SlabType, SlabAnalyzeRequest> = {
  aligerada_1d: defaultsFor('aligerada_1d'),
  maciza_1d: defaultsFor('maciza_1d'),
  aligerada_2d: defaultsFor('aligerada_2d'),
  maciza_2d: defaultsFor('maciza_2d'),
};

export const useSlabStore = create<SlabState>((set) => ({
  request: initialRequests.aligerada_1d,
  requests: initialRequests,
  setType: (type) =>
    set((s) => {
      const prevType = s.request.section.type;
      const savedRequests = { ...s.requests, [prevType]: s.request };
      const nextRequest = savedRequests[type] ?? defaultsFor(type);
      const fixed: SlabAnalyzeRequest = {
        ...nextRequest,
        section: { ...nextRequest.section, type },
      };
      return { requests: savedRequests, request: fixed };
    }),
  setSection: (patch) =>
    set((s) => ({ request: { ...s.request, section: { ...s.request.section, ...patch } } })),
  setMaterials: (patch) =>
    set((s) => ({ request: { ...s.request, materials: { ...s.request.materials, ...patch } } })),
  setLoads: (patch) =>
    set((s) => ({ request: { ...s.request, loads: { ...s.request.loads, ...patch } } })),
  setReinforcement: (patch) =>
    set((s) => ({
      request: { ...s.request, reinforcement: { ...s.request.reinforcement, ...patch } },
    })),
  loadRequest: (req: SlabAnalyzeRequest) =>
    set((s) => ({
      request: req,
      requests: { ...s.requests, [req.section.type]: req },
    })),
}));
